import React, { FC, ReactNode } from "react";
import PanelHeading from "./PanelHeading";
import CompletionList, { CompletionItem } from "./CompletionList";
import { ArrowRightIcon } from "./icons";

export interface ResultScreenProps {
  eyebrow: string;
  title: string;
  subtitle: string;
  items: CompletionItem[];
  /** Banners and callouts that sit between the checklist and the actions. */
  children?: ReactNode;
  downloadLabel: string;
  /**
   * Fetches the signed documents. Absent until they exist — before the tenant
   * signs there is nothing countersigned to hand over, so the button is shown
   * disabled rather than hidden: it says what they will get.
   */
  onDownload?: () => void;
  /** True while that fetch is running. */
  downloading?: boolean;
  ctaLabel: string;
  /**
   * Where the screen leads next. Absent when there is nowhere to go — the
   * arrival summary of a four-step journey is the end of it, and the
   * welcome-home screen closes the whole thing. The button is hidden rather
   * than disabled in that case: a disabled control says "not yet", and both of
   * these mean "never". Rendering it regardless left a tenant on the last
   * screen of their journey clicking a button that did nothing.
   */
  onContinue?: () => void;
  restartLabel: string;
  /**
   * Walks the flow again from the start. A design-review affordance, omitted
   * once a real reservation is behind the screen — a tenant has no reason to
   * restart their own onboarding.
   */
  onRestart?: () => void;
  /** Closing line shown under the actions — e.g. what happens next before arrival. */
  note?: string;
  className?: string;
}

/**
 * The shape both result screens share — the arrival summary that closes the
 * before-arrival flow, and the welcome-home screen that closes the whole
 * journey. Centred, sealed, with a recap and a pair of closing actions.
 */
const ResultScreen: FC<ResultScreenProps> = ({
  eyebrow,
  title,
  subtitle,
  items,
  children,
  downloadLabel,
  onDownload,
  downloading = false,
  ctaLabel,
  onContinue,
  restartLabel,
  onRestart,
  note,
  className = "",
}) => (
  <div className={`pt-[18px] text-center ${className}`}>
    <span
      className="mx-auto mb-5 flex h-[68px] w-[68px] items-center justify-center rounded-full bg-navy text-[27px] text-white shadow-[0_0_0_9px_#eee8dc]"
      aria-hidden="true"
    >
      ✓
    </span>

    <PanelHeading
      eyebrow={eyebrow}
      title={title}
      subtitle={subtitle}
      centered
    />

    <CompletionList items={items} className="mt-6" />

    {children}

    {/* The two closing actions read as one choice, so they stay on a single
        row. Both are allowed to shrink instead of wrapping: on a narrow phone
        the labels run to two lines and the buttons grow taller, which keeps
        the pair side by side rather than stacking them. */}
    <div className="mt-5 flex flex-nowrap items-stretch justify-center gap-2.5">
      <button
        type="button"
        onClick={onDownload}
        disabled={!onDownload || downloading}
        aria-busy={downloading}
        // Both states disable the button, but they mean different things: one
        // is "there is nothing to download yet", the other is "it is happening".
        // A not-allowed cursor on a download in progress reads as a refusal.
        //
        // py-[2.5px] gave a 16px-tall control. Fine to click, far too small to
        // tap; the phone gets a 44px target and the desktop keeps its height.
        className={`min-h-[44px] min-w-0 border border-[#d5cec3] bg-white px-[17px] py-[10px] text-[12px] font-semibold text-navy transition-colors hover:border-gold focus:outline-none focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-2 disabled:opacity-45 disabled:hover:border-[#d5cec3] min-[760px]:min-h-0 min-[760px]:py-[5px] min-[760px]:text-[12px] ${
          downloading ? "cursor-wait" : "disabled:cursor-not-allowed"
        }`}
      >
        ↓ {downloadLabel}
      </button>

      {onContinue && (
        <button
          type="button"
          onClick={onContinue}
          className="flex min-w-0 items-center justify-between gap-4 rounded-[4px] bg-navy px-[22px] py-[13px] text-[12px] font-extrabold tracking-[0.04em] text-white transition-colors hover:bg-navy-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-navy focus-visible:ring-offset-2 min-[760px]:min-w-[230px] min-[760px]:text-[13px]"
        >
          {ctaLabel}
          <ArrowRightIcon className="h-4 w-4 flex-none" />
        </button>
      )}
    </div>

    {/* Closing line under the actions — same treatment as the subtitle. */}
    {note && (
      <p className="mx-auto mt-5 max-w-[510px] text-[15px] font-medium leading-[1.7] text-[#6f7a85] min-[760px]:text-[14.5px]">
        {note}
      </p>
    )}

    {/* A preview affordance, like the DMS switch on step 3 — it exists so the
        flow can be walked again without reloading, and is left out entirely
        once a real tenant is on the screen. */}
    {onRestart && (
      <button
        type="button"
        onClick={onRestart}
        className="mt-[18px] text-[11px] min-[760px]:text-[9px] font-medium text-[#9a7137] underline transition-colors hover:text-gold focus:outline-none focus-visible:ring-2 focus-visible:ring-gold"
      >
        {restartLabel}
      </button>
    )}
  </div>
);

export default ResultScreen;
